// Field-level conditional visibility (show_when v2).
//
// Mirrored by backend/services/field_conditions.py — the two share a test
// table; change them in lockstep. Coercion semantics copied from
// lib/flowConditions.js:12-21 (kept local so this module has no deps).
//
// Schema v2 (stored on the field, backward compatible — legacy
// {field_id, op, value} remains readable forever via normalization; the
// builder writes v2 on next edit):
//
//   "show_when": {
//     "match": "all",              // "all" | "any"
//     "conditions": [
//       { "field_id": "<uuid>", "op": "equals", "value": "Yes", "value_type": "string" }
//     ]
//   }
//
// value_type: "string" | "number" | "count" — drives coercion and which
// operator set the builder offers. "count" evaluates against the answer's
// selected-item count: Array.isArray(v) ? v.length : (isEmpty(v) ? 0 : 1).

const toStr = (v) => {
    if (v === true) return 'yes';
    if (v === false) return 'no';
    if (v === null || v === undefined) return '';
    return String(v).toLowerCase().trim();
};
const toNum = (v) => { const n = Number(v); return Number.isNaN(n) ? 0 : n; };

export const isEmptyAnswer = (a) =>
    a === null || a === undefined || a === '' || (Array.isArray(a) && a.length === 0);

const toCount = (v) => (Array.isArray(v) ? v.length : (isEmptyAnswer(v) ? 0 : 1));

const LEGACY_OPS = { eq: 'equals', neq: 'not_equals', contains: 'contains' };

// Legacy {field_id, op, value} → v2 {match, conditions:[…]}; v2 passes through.
export function normalizeShowWhen(showWhen) {
    if (!showWhen) return null;
    if (Array.isArray(showWhen.conditions)) {
        const conditions = showWhen.conditions.filter(c => c && c.field_id);
        if (!conditions.length) return null;
        return { match: showWhen.match === 'any' ? 'any' : 'all', conditions };
    }
    if (!showWhen.field_id) return null;
    return {
        match: 'all',
        conditions: [{
            field_id: showWhen.field_id,
            op: LEGACY_OPS[showWhen.op] || showWhen.op || 'equals',
            value: showWhen.value,
            value_type: 'string',
        }],
    };
}

const NUM_CMP = {
    equals: (a, b) => a === b,
    not_equals: (a, b) => a !== b,
    greater_than: (a, b) => a > b,
    less_than: (a, b) => a < b,
    greater_equal: (a, b) => a >= b,
    less_equal: (a, b) => a <= b,
};

function strContains(raw, needle) {
    return Array.isArray(raw)
        ? raw.some(x => toStr(x).includes(needle))
        : toStr(raw).includes(needle);
}

function evalCondition(cond, answers) {
    const raw = answers[cond.field_id];
    const op = cond.op;
    if (op === 'is_empty') return isEmptyAnswer(raw);
    if (op === 'is_not_empty') return !isEmptyAnswer(raw);

    if (cond.value_type === 'count') {
        const cmp = NUM_CMP[op];
        return cmp ? cmp(toCount(raw), toNum(cond.value)) : true; // fail open
    }
    if (cond.value_type === 'number') {
        const cmp = NUM_CMP[op];
        return cmp ? cmp(toNum(raw), toNum(cond.value)) : true;
    }
    const a = toStr(raw);
    const b = toStr(cond.value);
    switch (op) {
        case 'equals': return a === b;
        case 'not_equals': return a !== b;
        case 'contains': return strContains(raw, b);
        case 'not_contains': return !strContains(raw, b);
        case 'starts_with': return a.startsWith(b);
        case 'ends_with': return a.endsWith(b);
        default: return true; // unknown operator never hides a field
    }
}

// Cascade-aware: if a condition's trigger field is itself hidden, its answer
// counts as empty. Cycle guard fails open (same posture as resolveStepId in
// flowConditions.js).
export function evaluateShowWhen(field, answers, fieldsById, _visiting = new Set()) {
    const sw = normalizeShowWhen(field.show_when);
    if (!sw) return true;
    if (_visiting.has(field.field_id)) return true;
    _visiting.add(field.field_id);
    try {
        const results = sw.conditions.map(cond => {
            const trigger = fieldsById ? fieldsById[cond.field_id] : null;
            let effective = answers;
            if (trigger && !evaluateShowWhen(trigger, answers, fieldsById, _visiting)) {
                effective = { ...answers, [cond.field_id]: undefined };
            }
            return evalCondition(cond, effective);
        });
        return sw.match === 'any' ? results.some(Boolean) : results.every(Boolean);
    } finally {
        _visiting.delete(field.field_id);
    }
}

// Renderability = not always-hidden AND passes show_when.
//
// Deliberately SEPARATE from evaluateShowWhen, which must not know about
// `hidden`:
//
//   * evaluateShowWhen answers "is this field's answer live?" — it drives the
//     cascade (a hidden trigger's answer counts as empty) and it drives answer
//     retention in pruneHidden / effective_answers.
//   * isRenderable answers "does this go in the DOM?"
//
// An always-hidden field must answer YES to the first and NO to the second.
// Folding `hidden` into evaluateShowWhen would (a) poison the cascade for any
// field conditioned on it and (b) get its value pruned — which defeats the
// entire point of a hidden field.
export function isRenderable(field, answers, fieldsById) {
    if (field?.hidden) return false;
    return evaluateShowWhen(field, answers, fieldsById);
}

// Set of visible field_ids across a flattened field list.
export function visibleFieldIds(fields, answers) {
    const fieldsById = {};
    fields.forEach(f => { if (f.field_id) fieldsById[f.field_id] = f; });
    return new Set(
        fields.filter(f => evaluateShowWhen(f, answers, fieldsById)).map(f => f.field_id)
    );
}
